import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { StepUpSpinner } from "../../../Components/Loader";
import ProfileTimelineSection from "../../../Components/CommunicationTimeline/ProfileTimelineSection";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm, confirmPresets } from "../../../Components/Confirm";
import useUser from "../../../Core/hook/useUser";
import {
  useShowParentQuery,
  useToggleParentChildAccessMutation,
  useRemoveParentChildMutation,
  useEnableParentPortalMutation,
  useResetParentPasswordMutation,
  useSendParentLoginDetailsMutation,
  useSendParentResetLinkMutation,
  useToggleParentAccessMutation,
} from "../../../Api/attoDeskApi";
import { StudentProfileLink } from "../../../Components/ProfileLinks";
import InvoiceHistorySection from "./InvoiceHistorySection";
import PaymentsBillingSection from "./PaymentsBillingSection";
import ParentCommunicationPanel from "./ParentCommunicationPanel";

/**
 * Premium Parent Profile.
 *
 * Kept the exact same data model, permissions, and actions (Edit Parent,
 * enable/disable child access, unlink child, Back to Parents). Everything
 * else is a full visual rewrite to match the soft-color / soft-shadow
 * design language used across the newer Finance and Dashboard pages:
 *   - Gradient hero with avatar-initials, name, contact, edit action
 *   - Four coloured summary cards (linked children, primary billing,
 *     reminder method, account status)
 *   - Icon-labelled contact + account details grid (no giant blank card)
 *   - Linked-children cards with clickable Student Profile links,
 *     relationship / primary-billing / access badges, tooltips on actions
 *   - Loading / empty / missing-data states are polished
 */
const initialsOf = (name?: string | null): string => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const StatusChip: React.FC<{ status?: string | null; size?: "small" | "medium" }> = ({
  status,
  size = "small",
}) => {
  const s = (status || "").toLowerCase();
  const isActive = s === "active";
  const isDisabled = s === "disabled";
  const label = isActive ? "Active" : isDisabled ? "Disabled" : "No Login";
  const bg = isActive ? "#dcfce7" : isDisabled ? "#fee2e2" : "#f1f5f9";
  const fg = isActive ? "#166534" : isDisabled ? "#991b1b" : "#475569";
  return (
    <Chip size={size} label={label} sx={{ bgcolor: bg, color: fg, fontWeight: 700 }} />
  );
};

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tint: string; // hex
}> = ({ icon, label, value, tint }) => (
  <Box
    sx={{
      borderRadius: "16px",
      p: 2,
      background: `linear-gradient(135deg, ${alpha(tint, 0.14)} 0%, ${alpha(tint, 0.04)} 100%)`,
      border: `1px solid ${alpha(tint, 0.24)}`,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      minHeight: 84,
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: alpha(tint, 0.2),
        color: tint,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.disabled" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: "text.primary", lineHeight: 1.25 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const DetailRow: React.FC<{ icon: React.ReactNode; label: string; value?: string | null }> = ({
  icon,
  label,
  value,
}) => (
  <Grid item xs={12} sm={6} lg={4}>
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        p: 1.5,
        borderRadius: "12px",
        border: "1px solid rgba(15,23,42,0.06)",
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ color: "text.disabled", pt: 0.25 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "text.primary", wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

const ChildCard: React.FC<{
  child: any;
  editable: boolean;
  onEnable: () => void;
  onDisable: () => void;
  onUnlink: () => void;
  onView: () => void;
}> = ({ child: c, editable, onEnable, onDisable, onUnlink, onView }) => {
  const theme = useTheme();
  const accessDisabled = c.access_status === "disabled";
  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        bgcolor: "#fff",
        p: 2,
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.75,
        transition: "box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": { boxShadow: "0 6px 20px rgba(15,23,42,0.06)" },
      }}
    >
      <Avatar
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.14),
          color: theme.palette.primary.main,
          width: 48,
          height: 48,
          fontWeight: 800,
        }}
      >
        {initialsOf(c.name)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <StudentProfileLink id={c.student_id} name={c.name} sx={{ fontSize: 15, fontWeight: 800 }}>
            {c.name}
          </StudentProfileLink>
          {c.isPrimary && (
            <Chip
              size="small"
              icon={<StarBorderRoundedIcon sx={{ fontSize: "14px !important" }} />}
              label="Primary Billing"
              sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#eef2ff", color: "#4f46e5" }}
            />
          )}
          <Chip
            size="small"
            label={accessDisabled ? "Access Disabled" : "Access Active"}
            sx={{
              height: 20,
              fontSize: 10.5,
              fontWeight: 700,
              bgcolor: accessDisabled ? "#fee2e2" : "#dcfce7",
              color: accessDisabled ? "#991b1b" : "#166534",
            }}
          />
        </Stack>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
          {[c.grade || "No grade", c.location || "No location", c.relationship_label || c.relationship_type || "Parent"]
            .filter(Boolean)
            .join(" • ")}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.75} alignSelf={{ xs: "flex-end", sm: "center" }} alignItems="center">
        <Tooltip title={`Open ${c.name}'s student profile`} arrow>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
            onClick={onView}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              borderColor: alpha(theme.palette.primary.main, 0.4),
              color: theme.palette.primary.main,
              px: 1.25,
              minWidth: 0,
              whiteSpace: "nowrap",
              "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.06) },
            }}
          >
            View Profile
          </Button>
        </Tooltip>
        {editable && accessDisabled && (
          <Tooltip title={`Enable ${c.name}'s access under this parent`} arrow>
            <IconButton size="small" onClick={onEnable} sx={{ bgcolor: "#ecfdf5", "&:hover": { bgcolor: "#d1fae5" } }}>
              <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#16a34a" }} />
            </IconButton>
          </Tooltip>
        )}
        {editable && !accessDisabled && (
          <Tooltip title={`Suspend parent access to ${c.name} (does not delete the student)`} arrow>
            <IconButton size="small" onClick={onDisable} sx={{ bgcolor: "#fff7ed", "&:hover": { bgcolor: "#ffedd5" } }}>
              <BlockIcon fontSize="small" sx={{ color: "#c2410c" }} />
            </IconButton>
          </Tooltip>
        )}
        {editable && (
          <Tooltip title={`Unlink ${c.name} from this parent (does not delete the student)`} arrow>
            <IconButton size="small" onClick={onUnlink} sx={{ bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" } }}>
              <LinkOffIcon fontSize="small" sx={{ color: "#dc2626" }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

const ParentDetail = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id = "" } = useParams();
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();
  const { canEdit } = useUser() as any;

  const { data, isLoading, refetch } = useShowParentQuery(id, { skip: !id });
  const [toggleChildAccess] = useToggleParentChildAccessMutation();
  const [removeChild] = useRemoveParentChildMutation();
  const [enablePortal, { isLoading: creatingAccount }] = useEnableParentPortalMutation();
  const [resetPassword, { isLoading: resettingPassword }] = useResetParentPasswordMutation();
  const [sendLoginDetails, { isLoading: sendingLogin }] = useSendParentLoginDetailsMutation();
  const [sendResetLink, { isLoading: sendingReset }] = useSendParentResetLinkMutation();
  const [toggleParentAccess, { isLoading: togglingAccess }] = useToggleParentAccessMutation();

  // One-time reveal for auto-generated temporary passwords returned by the
  // Create / Reset flows. The plaintext is only ever in memory here and is
  // discarded when the dialog closes; the backend has already hashed it.
  const [tempCredential, setTempCredential] = useState<
    | { title: string; username?: string; password: string; instructions: string }
    | null
  >(null);

  const parent = data?.data as any;
  const children = useMemo(() => (parent?.children as any[]) || [], [parent]);
  const editable = canEdit("Parent");

  const setChildAccess = async (studentId: number, status: "active" | "disabled", childName?: string) => {
    const run = async () => {
      const res = await toggleChildAccess({ parent_id: id, student_id: studentId, status }).unwrap();
      if (!res.status) throw new Error(res.message);
      refetch();
    };

    if (status === "disabled") {
      await confirm({
        ...confirmPresets.disableAccess(
          `Are you sure you want to disable this parent's access to ${childName || "this child"}?\nThe child record will not be affected.`
        ),
        successMessage: "Child access disabled.",
        onConfirm: run,
      });
      return;
    }

    try {
      await run();
      showMessage("Child access enabled.");
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to update child access");
    }
  };

  const handleCreatePortal = async () => {
    await confirm({
      title: "Create Parent Portal Account",
      message:
        "This will create a portal login for this parent and generate a temporary password. " +
        "The password will be shown once — copy it or immediately email login instructions.",
      confirmText: "Create Account",
      onConfirm: async () => {
        const res: any = await enablePortal(id).unwrap();
        if (!res?.status) throw new Error(res?.message || "Failed to create portal account.");
        refetch();
        if (res?.portal?.temp_password) {
          setTempCredential({
            title: "Portal Account Created",
            username: res.portal.username,
            password: res.portal.temp_password,
            instructions:
              "Share these credentials securely. The parent must change their password on first sign-in. " +
              "This password will not be shown again.",
          });
        }
      },
      successMessage: "Portal account created.",
    });
  };

  const handleSendLogin = async () => {
    try {
      const res: any = await sendLoginDetails(id).unwrap();
      if (res?.status) {
        showMessage(res.message || "Login instructions sent.");
        refetch();
      } else {
        showErrorMessage(res?.message || "Failed to send login instructions.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || e?.message || "Failed to send login instructions.");
    }
  };

  const handleSendReset = async () => {
    try {
      const res: any = await sendResetLink(id).unwrap();
      if (res?.status) {
        showMessage(res.message || "Password reset link sent.");
        refetch();
      } else {
        showErrorMessage(res?.message || "Failed to send reset link.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || e?.message || "Failed to send reset link.");
    }
  };

  const handleGenerateTempPassword = async () => {
    await confirm({
      title: "Generate Temporary Password",
      message:
        "This invalidates the parent's current password and issues a new one. " +
        "The temporary password will be shown once and must be changed on next login. " +
        "Prefer sending a self-service reset link when possible.",
      confirmText: "Generate",
      onConfirm: async () => {
        const res: any = await resetPassword(id).unwrap();
        if (!res?.status) throw new Error(res?.message || "Failed to reset password.");
        refetch();
        if (res?.portal?.temp_password) {
          setTempCredential({
            title: "Temporary Password Issued",
            username: res.portal.username,
            password: res.portal.temp_password,
            instructions:
              "Share this password securely with the parent. It will not be shown again, and the parent " +
              "must change it on next sign-in.",
          });
        }
      },
      successMessage: "Temporary password generated.",
    });
  };

  const handleTogglePortalStatus = async (next: "active" | "disabled") => {
    const isSuspend = next === "disabled";
    const run = async () => {
      const res: any = await toggleParentAccess({ parent_id: id, status: next }).unwrap();
      if (!res?.status) throw new Error(res?.message);
      refetch();
    };

    if (isSuspend) {
      await confirm({
        ...confirmPresets.disableAccount(),
        message:
          `Suspend portal login for ${parent?.name || "this parent"}?\n\n` +
          "The parent will be signed out of all sessions and unable to log in. " +
          "Linked children, invoice history, and account records remain intact.",
        confirmText: "Suspend Access",
        successMessage: "Portal access suspended.",
        onConfirm: run,
      });
      return;
    }

    try {
      await run();
      showMessage("Portal access enabled.");
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to enable portal access.");
    }
  };

  const copyText = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showMessage(`${label} copied.`);
    } catch {
      showErrorMessage(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const unlinkChild = async (studentId: number, childName?: string) => {
    await confirm({
      ...confirmPresets.unlink(
        `Are you sure you want to remove ${childName || "this child"} from this parent?\nThe child record will not be deleted.`
      ),
      confirmText: "Remove Link",
      successMessage: "Child link removed.",
      onConfirm: async () => {
        const res = await removeChild({ parent_id: id, student_id: studentId }).unwrap();
        if (!res.status) throw new Error(res.message);
        refetch();
      },
    });
  };

  if (isLoading || !parent) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
        <StepUpSpinner size="medium" />
      </Box>
    );
  }

  const linkedCount = children.length;
  const primaryCount = children.filter((c) => c.isPrimary).length;
  const reminderMethod = [
    parent.is_email_notification && "Email",
    parent.is_sms_notification && "SMS",
    parent.is_payment_reminder && "Payment",
  ]
    .filter(Boolean)
    .join(", ") || "None";

  return (
    <Box
      sx={{
        // Full-width, full-viewport-height white surface. Overrides any
        // pale-green tint inherited from an outer layout wrapper or global
        // pseudo-element so the empty area surrounding every card/section
        // renders as pure #FFFFFF. Only the page-level background changes —
        // hero gradient, summary card tints, section borders, badges,
        // shadows, and Invoice History colours remain exactly as before.
        bgcolor: "#FFFFFF",
        minHeight: "100vh",
        width: "100%",
        // Neutralise any global ::before / ::after gradient overlay that
        // would otherwise repaint the background before the cards render.
        "&::before, &::after": { display: "none" },
      }}
    >
      <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1440, mx: "auto" }}>
      {/* ── Top action row ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/parents/list")}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Back to Parents
        </Button>
        {editable && (
          <Button
            variant="contained"
            startIcon={<EditSharpIcon />}
            onClick={() => navigate(`/parents/edit/${id}`)}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", boxShadow: "none" }}
          >
            Edit Parent
          </Button>
        )}
      </Stack>

      {/* ── Hero ── */}
      <Box
        sx={{
          position: "relative",
          borderRadius: "20px",
          overflow: "hidden",
          background: `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha("#8b5cf6", 0.10)} 50%, ${alpha("#22c55e", 0.10)} 100%)`,
          border: "1px solid rgba(15,23,42,0.06)",
          p: { xs: 2.5, md: 3 },
          mb: 2.5,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={{ xs: 2, md: 3 }}
        >
          <Avatar
            sx={{
              width: { xs: 72, md: 88 },
              height: { xs: 72, md: 88 },
              bgcolor: theme.palette.primary.main,
              color: "#fff",
              fontSize: { xs: 24, md: 28 },
              fontWeight: 800,
              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            }}
          >
            {initialsOf(parent.name)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
              <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, letterSpacing: "-0.02em" }}>
                {parent.name}
              </Typography>
              <StatusChip status={parent.status} />
              <Chip
                size="small"
                label={parent.relationship_label || "Parent / Guardian"}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  color: theme.palette.primary.main,
                }}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 2.5 }} sx={{ mt: 1 }}>
              {parent.email && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <EmailOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{parent.email}</Typography>
                </Stack>
              )}
              {parent.phone && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <PhoneOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{parent.phone}</Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* ── Summary cards ── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            icon={<GroupsOutlinedIcon />}
            label="Linked Children"
            value={linkedCount}
            tint="#3b82f6"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            icon={<StarBorderRoundedIcon />}
            label="Primary Billing"
            value={primaryCount > 0 ? `${primaryCount} child${primaryCount > 1 ? "ren" : ""}` : "None"}
            tint="#8b5cf6"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            icon={<NotificationsActiveOutlinedIcon />}
            label="Reminders"
            value={reminderMethod}
            tint="#14b8a6"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            icon={<CheckCircleOutlineIcon />}
            label="Account"
            value={<StatusChip status={parent.status} />}
            tint="#22c55e"
          />
        </Grid>
      </Grid>

      {/* ── Contact + account details ── */}
      <Box
        sx={{
          borderRadius: "16px",
          border: "1px solid rgba(15,23,42,0.06)",
          bgcolor: "#f8fafc",
          p: 2,
          mb: 2.5,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "text.disabled", mb: 1.5 }}>
          Contact & Account
        </Typography>
        <Grid container spacing={1.5}>
          <DetailRow icon={<EmailOutlinedIcon fontSize="small" />} label="Email" value={parent.email} />
          <DetailRow icon={<PhoneOutlinedIcon fontSize="small" />} label="Phone" value={parent.phone} />
          <DetailRow icon={<PersonOutlineIcon fontSize="small" />} label="Username" value={parent.username} />
          <DetailRow icon={<HomeOutlinedIcon fontSize="small" />} label="Address" value={parent.address} />
          <DetailRow icon={<PublicOutlinedIcon fontSize="small" />} label="Country" value={
            parent.country_code === "CA" ? "Canada" :
            parent.country_code === "US" ? "United States" :
            "Not set"
          } />
          <DetailRow icon={<NotificationsActiveOutlinedIcon fontSize="small" />} label="Reminder Method" value={reminderMethod} />
          <DetailRow
            icon={<EventOutlinedIcon fontSize="small" />}
            label="Created"
            value={
              parent.created_at
                ? new Date(parent.created_at).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "Not available"
            }
          />
        </Grid>
      </Box>

      {/* ── Parent Portal Login & Access ── */}
      {(() => {
        const portal = parent.portal || {};
        const portalStatus: string = portal.status || (parent.has_account ? (parent.status === "active" ? "active" : "suspended") : "no_account");
        const statusMeta: Record<string, { label: string; bg: string; fg: string; icon: React.ReactNode }> = {
          active:      { label: "Active",             bg: "#dcfce7", fg: "#166534", icon: <VerifiedOutlinedIcon sx={{ fontSize: 16 }} /> },
          suspended:   { label: "Suspended",          bg: "#ffedd5", fg: "#9a3412", icon: <BlockIcon sx={{ fontSize: 16 }} /> },
          invitation_pending: { label: "Invitation Pending", bg: "#fef3c7", fg: "#92400e", icon: <SendOutlinedIcon sx={{ fontSize: 16 }} /> },
          no_account:  { label: "No Portal Account",  bg: "#ede9fe", fg: "#5b21b6", icon: <KeyOutlinedIcon sx={{ fontSize: 16 }} /> },
        };
        const meta = statusMeta[portalStatus] || statusMeta.no_account;
        const accessIds: number[] = Array.isArray(portal.access_student_ids) ? portal.access_student_ids : [];
        const accessNames = children.filter((c: any) => accessIds.includes(Number(c.student_id))).map((c: any) => c.name);
        const fmtDT = (iso?: string | null) => {
          if (!iso) return "Never";
          try {
            return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
          } catch { return "—"; }
        };
        return (
          <Box
            sx={{
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              bgcolor: "#f8fafc",
              p: 2,
              mb: 2.5,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <KeyOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "text.disabled" }}>
                  Parent Portal Login &amp; Access
                </Typography>
              </Stack>
              <Chip
                size="small"
                icon={<Box sx={{ display: "flex", color: meta.fg }}>{meta.icon}</Box>}
                label={meta.label}
                sx={{ height: 24, fontSize: 11.5, fontWeight: 800, bgcolor: meta.bg, color: meta.fg, "& .MuiChip-icon": { color: meta.fg, ml: "6px" } }}
              />
            </Stack>

            <Grid container spacing={1.5}>
              <DetailRow icon={<PersonOutlineIcon fontSize="small" />} label="Username" value={portal.username || parent.username || "—"} />
              <DetailRow icon={<EmailOutlinedIcon fontSize="small" />} label="Login Email" value={portal.login_email || parent.account_email || parent.email || "—"} />
              <DetailRow icon={<HistoryOutlinedIcon fontSize="small" />} label="Last Login" value={fmtDT(portal.last_login_at || parent.last_login)} />
              <DetailRow icon={<EventOutlinedIcon fontSize="small" />} label="Account Created" value={portal.account_created_at ? new Date(portal.account_created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"} />
              <DetailRow icon={<KeyOutlinedIcon fontSize="small" />} label="Password" value={portal.password_is_set ? "Set" : (parent.has_account ? "Not set" : "—")} />
              <DetailRow icon={<GroupsOutlinedIcon fontSize="small" />} label="Access" value={accessNames.length ? accessNames.join(", ") : (children.length ? "No children enabled" : "No linked children")} />
            </Grid>

            {(() => {
              // Shared button chrome — every action shares the same height,
              // padding, corner radius, and interaction rhythm so the row
              // reads as a single intentional control group.
              const btnBase = {
                textTransform: "none" as const,
                fontWeight: 800,
                borderRadius: "10px",
                px: 1.75,
                height: 36,
                minHeight: 36,
                borderWidth: "1.5px",
                boxShadow: "none",
                whiteSpace: "nowrap" as const,
                transition: "background-color .15s ease, box-shadow .15s ease, transform .12s ease",
                "&:hover": { transform: "translateY(-1px)" },
                "&.Mui-disabled": { opacity: 0.7 },
              };
              const softBtn = (bg: string, hoverBg: string, fg: string, border: string) => ({
                ...btnBase,
                bgcolor: bg,
                color: fg,
                border: `1.5px solid ${border}`,
                "&:hover": {
                  ...btnBase["&:hover"],
                  bgcolor: hoverBg,
                  borderColor: fg,
                  boxShadow: `0 4px 12px ${alpha(fg, 0.18)}`,
                },
                "&.Mui-disabled": { bgcolor: bg, color: alpha(fg, 0.65), borderColor: border, opacity: 0.85 },
              });
              const tipDenied = "You do not have permission to manage parent portal access.";

              // Shared spinner sized to sit inline with the label text.
              const spin = (color?: string) => (
                <CircularProgress size={14} sx={{ color: color || "inherit" }} />
              );

              return (
                <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1.25} sx={{ mt: 2 }}>
                  {portalStatus === "no_account" && (
                    <Tooltip title={editable ? "Create a Parent Portal login for this parent" : tipDenied} arrow>
                      <span>
                        <Button
                          onClick={handleCreatePortal}
                          disabled={!editable || creatingAccount}
                          startIcon={creatingAccount ? spin("#1e3a8a") : <KeyOutlinedIcon sx={{ fontSize: 18 }} />}
                          sx={softBtn("#dbeafe", "#bfdbfe", "#1e3a8a", "#bfdbfe")}
                        >
                          {creatingAccount ? "Creating Account…" : "Create Portal Account"}
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  {portalStatus !== "no_account" && (
                    <>
                      <Tooltip title={editable ? "Email the parent their username and a secure sign-in link" : tipDenied} arrow>
                        <span>
                          <Button
                            onClick={handleSendLogin}
                            disabled={!editable || sendingLogin}
                            startIcon={sendingLogin ? spin("#1e3a8a") : <SendOutlinedIcon sx={{ fontSize: 18 }} />}
                            sx={softBtn("#dbeafe", "#bfdbfe", "#1e3a8a", "#bfdbfe")}
                          >
                            {sendingLogin
                              ? "Sending Instructions…"
                              : portal.password_is_set
                                ? "Resend Login Instructions"
                                : "Send Login Instructions"}
                          </Button>
                        </span>
                      </Tooltip>

                      <Tooltip title={editable ? "Email the parent a secure self-service password reset link" : tipDenied} arrow>
                        <span>
                          <Button
                            onClick={handleSendReset}
                            disabled={!editable || sendingReset}
                            startIcon={sendingReset ? spin("#92400e") : <LockResetIcon sx={{ fontSize: 18 }} />}
                            sx={softBtn("#fef3c7", "#fde68a", "#92400e", "#fde68a")}
                          >
                            {sendingReset ? "Sending Reset…" : "Send Password Reset"}
                          </Button>
                        </span>
                      </Tooltip>

                      <Tooltip title={editable ? "Issue a one-time temporary password (shown once). Prefer sending a reset link when possible." : tipDenied} arrow>
                        <span>
                          <Button
                            onClick={handleGenerateTempPassword}
                            disabled={!editable || resettingPassword}
                            startIcon={resettingPassword ? spin("#475569") : <KeyOutlinedIcon sx={{ fontSize: 18 }} />}
                            sx={softBtn("#f1f5f9", "#e2e8f0", "#334155", "#e2e8f0")}
                          >
                            {resettingPassword ? "Generating…" : "Generate Temp Password"}
                          </Button>
                        </span>
                      </Tooltip>

                      {portalStatus === "active" ? (
                        <Tooltip title={editable ? "Suspend portal login. Preserves the parent, children, and history." : tipDenied} arrow>
                          <span>
                            <Button
                              onClick={() => handleTogglePortalStatus("disabled")}
                              disabled={!editable || togglingAccess}
                              startIcon={togglingAccess ? spin("#991b1b") : <BlockIcon sx={{ fontSize: 18 }} />}
                              sx={softBtn("#fee2e2", "#fecaca", "#991b1b", "#fecaca")}
                            >
                              {togglingAccess ? "Suspending…" : "Suspend Portal Access"}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={editable ? "Restore portal login for this parent" : tipDenied} arrow>
                          <span>
                            <Button
                              onClick={() => handleTogglePortalStatus("active")}
                              disabled={!editable || togglingAccess}
                              startIcon={togglingAccess ? spin("#166534") : <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
                              sx={softBtn("#dcfce7", "#bbf7d0", "#166534", "#bbf7d0")}
                            >
                              {togglingAccess ? "Enabling…" : "Enable Portal Access"}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Stack>
              );
            })()}
            <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 1.5 }}>
              Passwords are stored hashed and never displayed. Portal invitations and resets go through the secure email flow.
            </Typography>
          </Box>
        );
      })()}

      {/* ── Linked children ── */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
            Linked Children
          </Typography>
          <Chip
            size="small"
            label={`${linkedCount} linked`}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
          />
        </Stack>
        {children.length === 0 ? (
          <Box
            sx={{
              borderRadius: "16px",
              border: "1px dashed rgba(15,23,42,0.15)",
              bgcolor: "#fff",
              p: 4,
              textAlign: "center",
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.secondary" }}>
              No children are linked to this parent yet
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.disabled", mt: 0.5 }}>
              Link a student to see them here.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {children.map((c: any) => (
              <ChildCard
                key={c.student_id}
                child={c}
                editable={editable}
                onView={() => navigate(`/student-history/${c.student_id}`)}
                onEnable={() => setChildAccess(c.student_id, "active")}
                onDisable={() => setChildAccess(c.student_id, "disabled", c.name)}
                onUnlink={() => unlinkChild(c.student_id, c.name)}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* ── Communication (Email + SMS composer + history) ── */}
      <ParentCommunicationPanel
        parentId={id as any}
        parentEmail={(parent?.email as string) || null}
        parentPhone={(parent?.contact_number as string) || (parent?.phone as string) || null}
        parentName={(parent?.name as string) || ""}
      />

      {/* ── Invoice History (new) ──
             Fetches every invoice for this parent's linked students via
             POST /parent/{id}/invoices, with summary cards, filters and
             per-invoice expand-for-class-line-items. Superseded / voided /
             cancelled / deleted rows are excluded server-side so the count
             matches the Finance list. */}
      <InvoiceHistorySection parentId={id} childrenList={children} />

      {/* ── Payments & Billing (Batch 1D) ─────────────────────────
             Parent-owned Worldline profile, cards, per-child billing &
             auto-pay summary, and recent payments across all linked
             children. Backing endpoints under /api/admin/parents/{id}. */}
      <PaymentsBillingSection parentId={id as any} parentCountry={(parent?.country_code as "CA" | "US" | undefined) || null} />

      {/* ── Communication timeline (Pass E) ───────────────────────
             Reads /api/parents/{id}/communication-history. Scoped
             server-side to addresses this parent owns; unrelated
             recipient messages never appear here. */}
      <Box sx={{ mt: 3 }}>
        <ProfileTimelineSection profileType="parent" profileId={Number(id)} />
      </Box>
      </Box>

      {/* One-time temporary-password reveal. The plaintext lives only in this
          component's state and is discarded when the dialog closes. */}
      <Dialog
        open={!!tempCredential}
        onClose={() => setTempCredential(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{tempCredential?.title || "Temporary Credentials"}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            {tempCredential?.instructions}
          </Typography>
          {tempCredential?.username && (
            <TextField
              label="Username"
              value={tempCredential.username}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" onClick={() => copyText(tempCredential.username!, "Username")}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              sx={{ mb: 1.5 }}
            />
          )}
          {tempCredential?.password && (
            <TextField
              label="Temporary Password"
              value={tempCredential.password}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", fontWeight: 700 },
                endAdornment: (
                  <IconButton size="small" onClick={() => copyText(tempCredential.password, "Password")}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          )}
          <Typography sx={{ fontSize: 11.5, color: "#b45309", mt: 1.5, fontWeight: 700 }}>
            This password will not be shown again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTempCredential(null)} variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentDetail;
